<template>
  <div id="screen-scanning" :class="{ hidden: !isScanning }">
    <div class="m-2 absolute z-10">
      <z-button class="" @click="stopScan">
        {{ $t('Cancel') }}
      </z-button>
      <z-button class="ms-2" @click="onClickFlipCamera">
        {{ $t('Flip') }}
      </z-button>
    </div>
    <video id="camera-feed" ref="cameraFeed" playsinline></video>
    <canvas id="camera-feedback" ref="cameraFeedback"></canvas>
    <p id="camera-guides" ref="scanFeedback" class="absolute">
      {{ $t('PointCameraFrontSide') }}
    </p>
  </div>
</template>

<script>
import * as BlinkIDSDK from '@microblink/blinkid-in-browser-sdk'
import ZButton from '@/zain-component-library/src/lib-components/ZButton'
import microBlinkMixin from '@/mixins/microBlinkMixin'
import {PreferredCameraType} from "@microblink/blinkid-in-browser-sdk";

export default {
  name: 'MBScan',
  props: {
    docType: String,
    shouldStartScan: Boolean
  },
  components: { ZButton },
  mixins: [microBlinkMixin],
  data() {
    return {
      isBrowserSupported: false,
      isLoading: false,
      isScanning: false,
      scanFeedbackLock: false,
      sdk: null,
      recognizerRunner: null,
      drawContext: null,
      videoRecognizer: null,
      firstSideDone: false,
      scanResults: null,
      videoDevices: []
    }
  },
  methods: {
    async initMicroBlink() {
      this.isLoading = true
      if (BlinkIDSDK.isBrowserSupported()) {
        const loadSettings = new BlinkIDSDK.WasmSDKLoadSettings(
            process.env.VUE_APP_MB_LIC
        )
        this.drawContext = this.$refs.cameraFeedback.getContext('2d')
        loadSettings.allowHelloMessage = true
        let vm = this
        await BlinkIDSDK.loadWasmModule(loadSettings).then(
            (sdk) => {
              this.isLoading = false
              vm.sdk = sdk
              if (this.shouldStartScan) {
                // noinspection JSIgnoredPromiseFromCall
                vm.startScan()
              }
            },
            (error) => {
              this.isLoading = false
              console.error('Failed to load SDK!', error)
              this.$emit('close', 'Error, please try again later')
            }
        )
      } else {
        this.isLoading = false
        console.log('This browser is not supported by the SDK!')
        this.$emit('close', 'Error, This browser is not supported')
      }
    },

    updateScanFeedback(message, force) {
      if (this.scanFeedbackLock && !force) {
        return
      }

      this.scanFeedbackLock = true

      let m
      if (!this.firstSideDone) {
        m = 'Scan Front side of your ID \n'
      } else {
        m = 'Front Side is done ✅ please scan other side! \n'
      }

      this.$refs.scanFeedback.innerText = m + message

      window.setTimeout(() => (this.scanFeedbackLock = false), 1000)
    },

    async startScan() {
      try {
        this.isScanning = true

        const recognizer =
            this.docType === 'scan-id'
                ? await BlinkIDSDK.createBlinkIdCombinedRecognizer(this.sdk)
                : this.docType === 'barcode'
                ? await BlinkIDSDK.createIdBarcodeRecognizer(this.sdk)
                : await BlinkIDSDK.createBlinkIdRecognizer(this.sdk)

        const extraSettings = {}

        await recognizer.updateSettings({
          ...(await recognizer.currentSettings()),
          returnEncodedFullDocumentImage: true,
          ...extraSettings
        })

        let vm = this
        const callbacks = {
          onQuadDetection: (quad) => vm.drawQuad(quad),
          onDetectionFailed: () =>
              vm.updateScanFeedback('Detection failed', true),
          onFirstSideResult: () => (vm.firstSideDone = true)
        }

        this.recognizerRunner = await BlinkIDSDK.createRecognizerRunner(
            this.sdk,
            [recognizer],
            false,
            callbacks
        )

        this.videoRecognizer =
            await BlinkIDSDK.VideoRecognizer.createVideoRecognizerFromCameraStream(
                document.getElementById('camera-feed'),
                this.recognizerRunner,null,PreferredCameraType.BackFacingCamera
            )

        const processResult = await this.videoRecognizer.recognize()

        if (processResult !== BlinkIDSDK.RecognizerResultState.Empty) {
          const combinedResults = await recognizer.getResult()
          if (
              combinedResults.state !== BlinkIDSDK.RecognizerResultState.Empty
          ) {
            this.scanResults = combinedResults
          }
        } else {
          alert(this.$t('MicroBlinkError'))
        }

        this.videoRecognizer?.releaseVideoFeed()
        this.recognizerRunner?.delete()
        await recognizer?.delete()

        this.isScanning = false

        this.$emit('results', this.convertAllImagesToBase64(this.scanResults))

        this.isLoading = false
      } catch (e) {
        console.error(e)
        this.$emit('results', null)
      }
    },

    stopScan() {
      if (this.videoRecognizer) this.videoRecognizer.cancelRecognition()
      this.$emit('close')
    },

    drawQuad(quad) {
      this.setupColor(quad)
      this.setupMessage(quad)

      this.applyTransform(quad.transformMatrix)
      this.drawContext.beginPath()
      this.drawContext.moveTo(quad.topLeft.x, quad.topLeft.y)
      this.drawContext.lineTo(quad.topRight.x, quad.topRight.y)
      this.drawContext.lineTo(quad.bottomRight.x, quad.bottomRight.y)
      this.drawContext.lineTo(quad.bottomLeft.x, quad.bottomLeft.y)
      this.drawContext.closePath()
      this.drawContext.stroke()
    },

    applyTransform(transformMatrix) {
      const cameraFeedback = this.$refs.cameraFeedback
      const cameraFeed = this.$refs.cameraFeed
      const canvasAR = cameraFeedback.width / cameraFeedback.height
      const videoAR = cameraFeed.videoWidth / cameraFeed.videoHeight

      let xOffset = 0
      let yOffset = 0
      let scaledVideoHeight = 0
      let scaledVideoWidth = 0

      if (canvasAR > videoAR) {
        // pillar boxing: https://en.wikipedia.org/wiki/Pillarbox
        scaledVideoHeight = cameraFeedback.height
        scaledVideoWidth = videoAR * scaledVideoHeight
        xOffset = (cameraFeedback.width - scaledVideoWidth) / 2.0
      } else {
        // letter boxing: https://en.wikipedia.org/wiki/Letterboxing_(filming)
        scaledVideoWidth = cameraFeedback.width
        scaledVideoHeight = scaledVideoWidth / videoAR
        yOffset = (cameraFeedback.height - scaledVideoHeight) / 2.0
      }

      // first transform canvas for offset of video preview within the HTML video element (i.e. correct letter boxing or pillar boxing)
      this.drawContext.translate(xOffset, yOffset)
      // second, scale the canvas to fit the scaled video
      this.drawContext.scale(
          scaledVideoWidth / cameraFeed.videoWidth,
          scaledVideoHeight / cameraFeed.videoHeight
      )

      // finally, apply transformation from image coordinate system to
      // https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setTransform
      this.drawContext.transform(
          transformMatrix[0],
          transformMatrix[3],
          transformMatrix[1],
          transformMatrix[4],
          transformMatrix[2],
          transformMatrix[5]
      )
    },

    setupColor(displayable) {
      let color = '#FFFF00FF'

      if (displayable.detectionStatus === 0) {
        color = '#FF0000FF'
      } else if (displayable.detectionStatus === 1) {
        color = '#00FF00FF'
      }

      this.drawContext.fillStyle = color
      this.drawContext.strokeStyle = color
      this.drawContext.lineWidth = 5
    },

    setupMessage(displayable) {
      switch (displayable.detectionStatus) {
        case BlinkIDSDK.DetectionStatus.Fail:
          this.updateScanFeedback('Scanning...🔎')
          break
        case BlinkIDSDK.DetectionStatus.Success:
        case BlinkIDSDK.DetectionStatus.FallbackSuccess:
          this.updateScanFeedback('🔎')
          break
        case BlinkIDSDK.DetectionStatus.CameraAtAngle:
          this.updateScanFeedback('Adjust the angle')
          break
        case BlinkIDSDK.DetectionStatus.CameraTooHigh:
          this.updateScanFeedback('Move document closer')
          break
        case BlinkIDSDK.DetectionStatus.CameraTooNear:
        case BlinkIDSDK.DetectionStatus.DocumentTooCloseToEdge:
        case BlinkIDSDK.DetectionStatus.Partial:
          this.updateScanFeedback('Move document farther')
          break
        default:
          console.warn(
              'Unhandled detection status!',
              displayable.detectionStatus
          )
      }
    },

    async onClickFlipCamera() {
      const currentIndex = this.videoDevices.findIndex(
          (x) => x.deviceId === this.videoRecognizer.deviceId
      )

      if(currentIndex >= 0 && currentIndex <= this.videoDevices.length - 1) {
        const nextIndex = (currentIndex + 1) % this.videoDevices.length
        if(nextIndex !== currentIndex) {
          await this.videoRecognizer.changeCameraDevice(
              this.videoRecognizer[nextIndex]
          )
        }
      }
    }
  },
  async mounted() {
    try {
      this.isLoading = true
      const devices = await navigator.mediaDevices.enumerateDevices()
      this.videoDevices = devices.filter((device) => device.kind === 'videoinput')
      await this.initMicroBlink()
      this.isLoading = false
    } catch (e) {
      this.isLoading = false
    }
  }
}
</script>
